import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { LONDON_AREAS } from "@/lib/london-areas";
import { SERVICES } from "@/lib/services-catalog";
import { breadcrumb, faqPage } from "@/lib/seo-jsonld";
import { ArrowRight, MapPin, ShieldCheck, Star } from "lucide-react";

const TITLE = "Trusted Helpers across Greater London — Peopels.com";
const DESCRIPTION = "Find vetted local Helpers for handyman work, TV mounting, furniture assembly and moving help across every London borough. ID-verified, reviewed, and only paid when the job is done.";

const HUB_FAQS = [
  {
    q: "Which areas of London do you cover?",
    a: "Peopels.com currently covers all of Greater London — from west London neighbourhoods like Ealing, Chiswick and Hammersmith to north London (Camden, Islington), east (Hackney, Greenwich) and south (Clapham, Richmond, Kingston, Wimbledon).",
  },
  {
    q: "Are Peopels.com Helpers vetted?",
    a: "Yes. Every Helper passes ID verification and KYC checks. We hold payment in escrow until the job is marked done and reviewed.",
  },
  {
    q: "How much do London Helpers charge?",
    a: "Hourly rates in Greater London typically start from £25/hr for moving help, £25–£50/hr for furniture assembly, £30–£60/hr for handyman work and £35–£70/hr for TV mounting. You see an estimate before booking and only pay when the job is complete.",
  },
  {
    q: "How quickly can I get matched?",
    a: "Most London jobs are matched within a few minutes during normal hours. Urgent jobs are prioritised to nearby Helpers with strong reviews.",
  },
];

export const Route = createFileRoute("/london")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/london" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/london" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Greater London", path: "/london" },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqPage(HUB_FAQS)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Peopels.com",
          url: "/london",
          areaServed: { "@type": "AdministrativeArea", name: "Greater London" },
          priceRange: "££",
          description: DESCRIPTION,
        }),
      },
    ],
  }),
  component: LondonHub,
});

function LondonHub() {
  const sorted = [...LONDON_AREAS].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <Breadcrumbs items={[{ label: "Greater London" }]} />

        <header className="mb-8">
          <p className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-primary">
            <MapPin className="h-3.5 w-3.5" /> Greater London only
          </p>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-5xl">
            Trusted local Helpers across Greater London
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
            {DESCRIPTION}
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1"><ShieldCheck className="h-3 w-3 text-primary" /> ID-verified Helpers</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1">Stripe escrow payments</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1">Funds released when the job is done</span>
          </div>
        </header>

        <section aria-labelledby="services-h" className="mb-12">
          <h2 id="services-h" className="font-display text-2xl font-semibold">Services across London</h2>
          <p className="mt-1 text-sm text-muted-foreground">Four core services, every Greater London area.</p>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/post-job"
                  search={{ category: s.slug }}
                  className="block rounded-2xl border border-border bg-card p-5 shadow-card transition hover:border-primary/40"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold">{s.name}</h3>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{s.summary}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    From <span className="font-semibold text-foreground">£{s.rateLow}/hr</span> · typically {Math.round(s.avgDurationMinutes / 60 * 10) / 10} hr
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="areas-h" className="mb-12">
          <h2 id="areas-h" className="font-display text-2xl font-semibold">Areas we cover</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {LONDON_AREAS.length} popular London neighbourhoods — and growing.
          </p>
          <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {sorted.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/london/$area"
                  params={{ area: a.slug }}
                  className="block rounded-xl border border-border bg-card px-3 py-2.5 text-sm transition hover:border-primary/40"
                >
                  <span className="block font-medium">{a.name}</span>
                  <span className="block text-[11px] text-muted-foreground">{a.postcodes.join(" · ")}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="faq-h" className="mb-12">
          <h2 id="faq-h" className="font-display text-2xl font-semibold">London Helpers — FAQs</h2>
          <dl className="mt-4 space-y-3">
            {HUB_FAQS.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-4 open:shadow-card">
                <summary className="cursor-pointer list-none text-sm font-medium">
                  {f.q}
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </dl>
        </section>

        <section className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-card to-card p-6 text-center shadow-card">
          <h2 className="font-display text-xl font-semibold">Ready to get matched in London?</h2>
          <p className="mt-1 text-sm text-muted-foreground">Free to post, with payment held in escrow and released only when the job is done.</p>
          <Link
            to="/post-job"
            className="mt-4 inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Post a job
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
